import {
  CarrierType,
  CarrierVisitDto,
  CarrierVisitStatus,
  CarrierVisitsApi,
  EquipmentDto,
  EquipmentType,
  GangResponseDto,
  OperationType,
  OperatorViewSettings,
} from '@operations/app/api'
import { createApiClient } from '@operations/app/http-client'
import { EquipmentStore } from '@operations/stores/EquipmentStore'
import _ from 'lodash'

import { EquipmentOperatorAssignedPayload } from '@operations/app/api/signalRDtos/EquipmentOperatorAssignedPayload'
import Permission from '@operations/app/models/permission.enum'
import { EventTypes } from '@operations/messages/eventsTypes'
import { GangStore } from '@operations/stores/GangStore'
import { TenantConfigStore } from '@operations/stores/TenantConfigStore'
import { AppStore, AuthStore, IEvent, IMessageBus, PATHS } from '@tom-ui/utils'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

export interface EquipmentOperatorLandingFormData {
  equipmentId?: number
  vesselVisit: CarrierVisitDto | null
  operationType?: OperationType | null
  gang?: GangResponseDto | null
}

export class EquipmentOperatorLandingUIStore {
  loadingSignalR = false

  readyForOperationVesselVisits: CarrierVisitDto[] = []

  selectedEquipmentTypes?: EquipmentType[]
  selectedEquipmentId?: number
  selectedVesselVisit: CarrierVisitDto | null = null
  selectedOperationType: OperationType | null = null
  selectedGang?: GangResponseDto | null

  constructor(
    private readonly messageBus: IMessageBus,
    private readonly appStore: AppStore,
    private readonly authStore: AuthStore,
    private readonly equipmentStore: EquipmentStore,
    private readonly tenantConfigStore: TenantConfigStore,
    private readonly gangStore: GangStore,
  ) {
    makeObservable(this, {
      selectedEquipmentTypes: observable,
      selectedEquipmentId: observable,
      selectedVesselVisit: observable,
      readyForOperationVesselVisits: observable,
      selectedOperationType: observable,
      selectedGang: observable,

      selectEquipmentTypes: action,
      selectEquipment: action,
      selectVesselVisit: action,
      selectOperationType: action,
      selectGang: action,
      reset: action,

      equipmentOptions: computed,
      displayVesselVisitSelector: computed,
      displayOperationsTypeSelector: computed,
      selectedEquipment: computed,
      gangOptions: computed,
      warningForUserName: computed,
      isFulfilled: computed,
    })

    this.messageBus.subscribeEvent(EventTypes.CarrierVisitRefresh, this.reloadVesselVisitsBySignalR)
    this.messageBus.subscribeEvent<EquipmentOperatorAssignedPayload>(
      EventTypes.EquipmentOperatorAssigned,
      (res: IEvent<EquipmentOperatorAssignedPayload>) => {
        this.logoutUserFromEquipment(res)
        this.updateAssignedEquipmentOperator(res)
      },
    )
  }

  public selectEquipmentTypes(types?: EquipmentType[]): void {
    this.selectedEquipmentTypes = types
  }

  public selectEquipment(id?: number): void {
    if (this.selectedEquipmentId !== id) {
      this.selectedEquipmentId = id
    }
  }

  public selectVesselVisit(vesselVisit: CarrierVisitDto | null): void {
    if (this.selectedVesselVisit !== vesselVisit) {
      this.selectedVesselVisit = vesselVisit
    }
  }

  public selectOperationType(operationType: OperationType | null): void {
    if (this.selectedOperationType !== operationType) {
      this.selectedOperationType = operationType
    }
  }

  public selectGang(gang?: GangResponseDto | null): void {
    if (this.selectedGang !== gang) {
      this.selectedGang = gang
    }
  }

  public reset(): void {
    this.selectEquipment()
    this.selectVesselVisit(null)
    this.selectOperationType(null)
    this.selectGang()
  }

  public get equipmentOptions(): EquipmentDto[] {
    return _(
      this.equipmentStore.items.filter(
        x => this.selectedEquipmentTypes?.includes(x.equipmentType) && x.isActive,
      ),
    )
      .sortBy(i => i.name)
      .value()
  }

  public get gangOptions() {
    return _(this.gangStore.items)
      .sortBy(i => i.name)
      .value()
  }

  public get warningForUserName() {
    if (
      this.authStore.hasPermission(Permission.LogOutOtherOperators) &&
      this.selectedEquipment?.lastOperatorUserId !== this.authStore.userId &&
      this.selectedEquipment?.lastOperatorUserName
    ) {
      return this.selectedEquipment?.lastOperatorUserName
    }
  }

  public get selectedEquipment(): EquipmentDto | undefined {
    return this.equipmentStore.items.find(x => x.id === this.selectedEquipmentId)
  }

  get isFulfilled() {
    return (
      !!this.selectedEquipmentId &&
      (this.tenantConfigStore.skipGangSelection || !!this.selectedGang)
    )
  }

  private getEquipmentTypeSettings = (): OperatorViewSettings | undefined => {
    const equipmentType = this.selectedEquipment?.equipmentType
    if (!equipmentType) return

    return this.tenantConfigStore.operatorViewSettings?.[equipmentType]
  }

  public get displayVesselVisitSelector(): boolean {
    const equipmentTypeSettings = this.getEquipmentTypeSettings()

    if (!equipmentTypeSettings) {
      return false
    }

    return equipmentTypeSettings.displayVesselVisitSelector
  }

  public get displayOperationsTypeSelector(): boolean {
    const equipmentTypeSettings = this.getEquipmentTypeSettings()

    if (!equipmentTypeSettings) {
      return false
    }

    return equipmentTypeSettings.displayOperationsTypeSelector
  }

  private readonly logoutUserFromEquipment = (res: IEvent<EquipmentOperatorAssignedPayload>) => {
    const data = res.payload
    if (
      this.selectedEquipmentId === data?.equipmentId &&
      this.authStore.userId === data?.userId &&
      this.selectedEquipmentId !== data?.equipmentId
    ) {
      this.selectEquipment()
    }
  }

  async loadReadyForOperationVesselVisits() {
    const { data } = await createApiClient(CarrierVisitsApi).get(CarrierType.Vessel, undefined, [
      CarrierVisitStatus.InOperation,
    ])
    runInAction(() => (this.readyForOperationVesselVisits = data))
  }

  private readonly updateAssignedEquipmentOperator = (
    res: IEvent<EquipmentOperatorAssignedPayload>,
  ) => {
    const equipment = this.equipmentStore.items.find(item => item.id === res.payload?.equipmentId)

    if (equipment) {
      this.equipmentStore.updateStoreItem(
        {
          ...equipment,
          lastOperatorUserId: res.payload?.userId,
          lastOperatorUserName: res.payload?.userName,
        },
        equipment.id,
      )
    }
  }

  reloadVesselVisitsBySignalR = async () => {
    if (!this.displayVesselVisitSelector) return
    if (!this.loadingSignalR) {
      this.loadingSignalR = true
      await this.appStore.triggerReloadBySignalR(
        this.loadReadyForOperationVesselVisits,
        PATHS.operators.operator.url,
      )
      this.loadingSignalR = false
    }
  }
}
